import { type CSSProperties, type FC, type ReactNode, useId, useMemo, useRef, useState } from 'react';

import { brandBox } from '@Eneco/spark-tokens/eneco';

import {
  backdropClass,
  contentClass,
  labelClass,
  titleClass,
  rootClass,
  shapeClass,
  shapeSvgClass,
} from './PromotionalCard.css';
import { buildStickerShape, measureLines } from './stickerEngine';
import { STICKER_SIZE_PRESETS, type StickerSize } from './stickerSizePresets';
import { useIsomorphicLayoutEffect } from '../../hooks';
import { getClassNames } from '../../util/css';
import type { BorderRadiusRecipeVariants } from '../../util/css/vanilla-extract/shared/border.css';
import { Box } from '../Box/Box';
import { Stack } from '../Stack/Stack';

type PromotionalCardBackdropBorderRadius = BorderRadiusRecipeVariants['borderRadius'];

export type PromotionalCardBackground = 'white' | 'black' | 'brand';
export type PromotionalCardTitle =
  readonly [string, string] | readonly [string, string, string] | readonly [string, string, string, string];
export type PromotionalCardSize = StickerSize;

export interface PromotionalCardProps {
  /** Title lines (2–4). Each entry is one heading line. */
  title?: PromotionalCardTitle;
  /** Heading level. Defaults to `h2`. */
  headingAs?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /** Optional pill label under the title (not a link). */
  label?: string;
  /**
   * Shape fill.
   * - `white` / `black` — solid
   * - `brand` — gradient from brand red to orange500
   */
  background?: PromotionalCardBackground;
  /**
   * Campaign typography and shape padding (`s` | `m` | `l` | `xl`).
   * The parent is responsible for choosing the size (e.g. from viewport or layout).
   */
  size: PromotionalCardSize;
  /** Content behind the shape (visible through the right-side cutouts). */
  children?: ReactNode;
  className?: string;
  /** Extra class on the shape box (Storybook size helpers). */
  shapeClassName?: string;
  /** Corner radius on the backdrop layer (default `xl`). Sparky `s` = 8px on the 4px grid. */
  backdropBorderRadius?: Exclude<PromotionalCardBackdropBorderRadius, undefined>;
}

const DEFAULT_TITLE: PromotionalCardTitle = ['Nog schoner', 'wassen op', 'schone energie'];

/** Brand panel with text-driven silhouette, title, optional label and backdrop. */
export const PromotionalCard: FC<PromotionalCardProps> = ({
  title = DEFAULT_TITLE,
  headingAs = 'h2',
  label,
  background = 'brand',
  size,
  children,
  className,
  shapeClassName,
  backdropBorderRadius = 'xl',
}) => {
  const gradientId = useId();
  const shapeRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [path, setPath] = useState('');
  const [viewBox, setViewBox] = useState('0 0 0 0');
  const [gradientCoords, setGradientCoords] = useState({ x1: 0, y1: 0, x2: 1, y2: 1 });

  const preset = STICKER_SIZE_PRESETS[size];
  const isLight = background === 'white';
  const shapeFill = background === 'brand' ? `url(#${gradientId})` : 'currentColor';

  const styleVars = useMemo(
    () =>
      ({
        '--pc-font-size': `${preset.font}px`,
        '--pc-line-height': String(preset.lineHeight),
        '--pc-label-height': `${preset.labelHeight}px`,
        '--pc-label-font-size': `${preset.labelFont}px`,
        '--pc-label-padding-inline': `${preset.labelPaddingInline}px`,
      }) as CSSProperties,
    [preset.font, preset.labelFont, preset.labelHeight, preset.labelPaddingInline, preset.lineHeight],
  );

  useIsomorphicLayoutEffect(() => {
    const shapeElement = shapeRef.current;
    const contentElement = contentRef.current;
    if (!shapeElement || !contentElement) return;

    let isMounted = true;

    const updateShape = () => {
      if (!isMounted) return;

      const measured = measureLines(contentElement, shapeElement);
      const result = buildStickerShape(
        measured,
        {
          radius: preset.radius,
          padX: preset.padding,
          padY: preset.padding,
          padShelfY: preset.padShelfY,
          hasLabel: Boolean(label),
          snap: 0,
          fontSize: preset.font,
        },
        shapeElement.clientWidth,
        shapeElement.clientHeight,
      );

      if (!result) {
        setPath('');
        return;
      }

      setPath(result.path);
      setViewBox(`0 0 ${result.viewWidth} ${result.viewHeight}`);
      setGradientCoords(result.gradient);
    };

    updateShape();

    const observer = new ResizeObserver(updateShape);
    observer.observe(shapeElement);
    observer.observe(contentElement);

    void document.fonts?.ready.then(() => {
      updateShape();
    });

    return () => {
      isMounted = false;
      observer.disconnect();
    };
  }, [label, preset.font, preset.padShelfY, preset.padding, preset.radius, title]);

  const TitleTag = headingAs;

  return (
    <Box as="article" className={getClassNames(rootClass, className)}>
      {children ? (
        <Box overflow="hidden" borderRadius={backdropBorderRadius} className={backdropClass}>
          {children}
        </Box>
      ) : null}

      <div className={getClassNames(shapeClass({ background }), shapeClassName)} ref={shapeRef} style={styleVars}>
        <svg
          className={shapeSvgClass}
          viewBox={viewBox}
          preserveAspectRatio="none"
          aria-hidden
          data-testid="promotional-card-shape">
          {background === 'brand' ? (
            <defs>
              <linearGradient
                data-testid="promotional-card-shape-gradient"
                id={gradientId}
                gradientUnits="userSpaceOnUse"
                x1={gradientCoords.x1}
                y1={gradientCoords.y1}
                x2={gradientCoords.x2}
                y2={gradientCoords.y2}>
                <stop offset="0%" stopColor={brandBox.start} />
                <stop offset="100%" stopColor={brandBox.end} />
              </linearGradient>
            </defs>
          ) : null}
          <path d={path} fill={shapeFill} data-testid="promotional-card-shape-path" />
        </svg>

        {title || label ? (
          <Stack ref={contentRef} inline direction="column" alignX="start" gap={0} className={contentClass({ size })}>
            {title ? (
              <TitleTag className={titleClass({ tone: isLight ? 'light' : 'dark' })}>{title.join('\n')}</TitleTag>
            ) : null}

            {label ? <span className={labelClass({ background })}>{label}</span> : null}
          </Stack>
        ) : null}
      </div>
    </Box>
  );
};

export default PromotionalCard;
